import { useState, useContext, createContext, useCallback, useEffect } from "react";
import { decryptData, setDecryptedRoleCache } from "../Utils/stringUtls";

export const AuthContext = createContext({
  user: null,
  isTempPwd: null,
  token: null,
  passwordReset: false,
  setUser: () => {},
  setToken: () => {},
  setUserRole: () => {},
  setDarkMode: () => {},
  setIsTempPwd: () => {},
  setPasswordReset: false,
  session: "",
  darkMode: false,
  setSession: "",
  sessionCount: "",
  setSessionCount: "",
  userRole: "",
  roleName: "",
  isAuthReady: false,
});

const clearRoleStorage = () => {
  localStorage.removeItem("userRole");
  localStorage.removeItem("role");
  localStorage.removeItem("userRoleDecrypted");
  localStorage.removeItem("roleDecrypted");
  setDecryptedRoleCache("", "");
};

export const ContextProvider = ({ children }) => {
  const [user, _setUser] = useState({});
  const [token, _setToken] = useState(localStorage.getItem("token"));
  const [userRole, _setUserRole] = useState("");
  const [roleName, _setRoleName] = useState("");
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [darkMode, _setDarkMode] = useState(
    JSON.parse(localStorage.getItem("darkMode")),
  );
  const [isTempPwd, _setIsTempPwd] = useState(
    localStorage.getItem("isTempPwd"),
  );
  const [session, _setSession] = useState(localStorage.getItem("session"));
  const [sessionCount, _setSessionCount] = useState(
    localStorage.getItem("sessionCount"),
  );
  const [passwordReset, _setPasswordReset] = useState(
    localStorage.getItem("reset_state"),
  );

  const setUser = (user) => {
    _setUser(user);
    if (user) {
      localStorage.setItem("user", JSON.stringify(user));
    } else {
      localStorage.removeItem("user");
    }
  };
  const setDarkMode = (mode) => {
    _setDarkMode(mode);
    if (mode) {
      localStorage.setItem("darkMode", JSON.stringify(mode));
    } else {
      localStorage.removeItem("darkMode");
    }
  };
  const setToken = (token) => {
    _setToken(token);
    if (token) {
      localStorage.setItem("token", token);
    } else {
      localStorage.removeItem("token");
    }
  };

  const decryptAndCacheRoles = useCallback(async (encryptedRoleId, encryptedRoleName) => {
    const [decryptedRoleId, decryptedRoleName] = await Promise.all([
      encryptedRoleId ? decryptData(encryptedRoleId) : Promise.resolve(""),
      encryptedRoleName ? decryptData(encryptedRoleName) : Promise.resolve(""),
    ]);
    const resolvedRoleName = decryptedRoleName || roleName || "";

    if (encryptedRoleId && !decryptedRoleId) {
      clearRoleStorage();
      _setUserRole("");
      _setRoleName("");
      return { roleId: "", roleName: "" };
    }

    _setUserRole(decryptedRoleId);
    _setRoleName(resolvedRoleName);
    setDecryptedRoleCache(decryptedRoleId, resolvedRoleName);
    localStorage.removeItem("userRoleDecrypted");
    localStorage.removeItem("roleDecrypted");

    return { roleId: decryptedRoleId, roleName: resolvedRoleName };
  }, [roleName]);

  useEffect(() => {
    let isMounted = true;

    const loadStoredRoles = async () => {
      const encryptedRoleId = localStorage.getItem("userRole");
      const encryptedRoleName = localStorage.getItem("role");

      if (!encryptedRoleId && !encryptedRoleName) {
        clearRoleStorage();
        if (isMounted) {
          _setUserRole("");
          _setRoleName("");
          setIsAuthReady(true);
        }
        return;
      }

      const roles = await decryptAndCacheRoles(encryptedRoleId, encryptedRoleName);

      if (isMounted) {
        _setUserRole(roles.roleId);
        _setRoleName(roles.roleName);
        setIsAuthReady(true);
      }
    };

    loadStoredRoles();

    return () => {
      isMounted = false;
    };
  }, [decryptAndCacheRoles]);

  const setUserRole = async (encryptedRoleId, encryptedRoleName) => {
    setIsAuthReady(false);

    if (encryptedRoleId) {
      localStorage.setItem("userRole", encryptedRoleId);
    } else {
      localStorage.removeItem("userRole");
    }
    if (encryptedRoleName) {
      localStorage.setItem("role", encryptedRoleName);
    } else {
      localStorage.removeItem("role");
    }

    const roles = await decryptAndCacheRoles(encryptedRoleId, encryptedRoleName);
    setIsAuthReady(true);
    return roles;
  };

  const setIsTempPwd = (boolValue) => {
    _setIsTempPwd(boolValue);
    if (boolValue) {
      localStorage.setItem("isTempPwd", boolValue);
    } else {
      localStorage.removeItem("isTempPwd");
    }
  };
  const setSession = (sessionToken) => {
    _setSession(sessionToken);
    if (sessionToken) {
      localStorage.setItem("session", sessionToken);
    } else {
      localStorage.removeItem("session");
    }
  };
  const setSessionCount = (count) => {
    _setSessionCount(count);
    if (count) {
      localStorage.setItem("sessionCount", count);
    } else {
      localStorage.removeItem("sessionCount");
    }
  };
  const setPasswordReset = (state) => {
    _setPasswordReset(state);
    if (state) {
      localStorage.setItem("reset_state", state);
    } else {
      localStorage.removeItem("reset_state");
    }
  };
  return (
    <AuthContext.Provider
      value={{
        user,
        isTempPwd,
        token,
        setUser,
        darkMode,
        setDarkMode,
        setIsTempPwd,
        setToken,
        passwordReset,
        setPasswordReset,
        roleName,
        isAuthReady,
        session,
        setSession,
        sessionCount,
        setSessionCount,
        setUserRole,
        userRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const UserStateContext = () => useContext(AuthContext);
